// shared/src/schemas/macro-prediction-emit.ts
// Phase 12.2.H — LLM-emit shapes for m.1/m.3 macro predictions.
//
// These are the EMIT-side schemas (what the LLM returns). The orchestrator
// (chain-workflow m.1.analyst / m.3.synthesis stages) fills in the DB-only
// columns (id, brief_id, emitting_stage, created_at) and INSERTs into
// macro_predictions + macro_prediction_observables.
//
// Mirror but slim down the DB schemas at:
//   · macro-prediction.ts          (16-col DB row → 12 emit fields)
//   · macro-prediction-observable.ts (8-col DB row → 6 emit fields)
//
// Multi-observable rules enforced (≥3 observables, ≥2 asset classes
// detected via $-prefix vs bare) in chain-stage post-parse, not at Zod
// level — Zod validates shape, business rules apply after extraction.

import { z } from 'zod';
import {
  MacroPredictionEmittingStageEnum,
  MacroPredictionModeEnum,
} from './macro-prediction.js';
import { MacroPredictionObservableDirectionEnum } from './macro-prediction-observable.js';

// Re-export the enum used by orchestrators to set emitting_stage on insert.
export { MacroPredictionEmittingStageEnum, MacroPredictionModeEnum };
export { MacroPredictionObservableDirectionEnum };

// ── Per-observable emit shape ─────────────────────────────────────────────

// 2026-05-26 — scale-range guards for known indicators. Catches the LLM
// emitting TNX=45.6 (×10 scale) when daily_closes stores TNX=4.49 (raw
// yield), which produced −90% drift on the deployed dashboard. Each
// range = (min, max). An indicator whose baseline_value falls outside its
// range fails Zod validation and the chain rejects the emit. Per
// [[feedback_no_fallbacks]] — fail-loud, never silently rescale.
//
// Equity ETFs and FX pairs aren't in this table because their ranges are
// too wide / scale-stable; the .positive() guard above is enough for them.
export const INDICATOR_SCALE_RANGES: Readonly<Record<string, readonly [number, number]>> = {
  // Yields (RAW percent, NOT ×10).
  TNX:   [1.0,   10.0],
  TYX:   [1.0,   10.0],
  DGS10: [1.0,   10.0],
  DGS2:  [0.0,   10.0],
  DGS30: [1.0,   10.0],
  // Vol indices.
  VIX:   [8.0,   80.0],
  VVIX:  [50.0,  200.0],
  MOVE:  [40.0,  300.0],
  // FX / dollar.
  DXY:   [70.0,  130.0],
  // Crypto-dominance ratios (percent, 0-100).
  'BTC.DOM':   [20.0, 80.0],
  'ETH.DOM':   [5.0,  40.0],
  // Sentiment.
  FEAR_GREED: [0.0, 100.0],
};

export const PredictedObservableEmitSchema = z.object({
  indicator:        z.string().min(1).max(10),
  // World-state Phase 2 (2026-05-26, #78): tighten to .positive() so the
  // chain fails-loud when the LLM emits baseline_value=0 (legacy posture
  // accepted 0 as a placeholder for "indicator price unknown"). Per the
  // brief revision: world-state geometry cannot anchor at baseline=0
  // (target = 0 × (1+pct) = 0 always), and persisting 0 corrupts the
  // projection. Per [[feedback_no_fallbacks]] fail-loud is the correct
  // posture — drop the observable rather than emit a sentinel.
  baseline_value:   z.number().positive(),
  direction:        MacroPredictionObservableDirectionEnum,
  change_pct:       z.number(),
  change_ci_low:    z.number(),
  change_ci_high:   z.number(),
  // World-state Phase 1 (2026-05-25): LLM marks the 3-5 most diagnostic
  // observables across all predictions per brief. Headline-marked
  // observables drive the ZoneTheBet WorldStateComparison visualization.
  // Mirror marks across paired contrarian + consensus predictions
  // (enforced at macro-prediction-write boundary). Optional because
  // the schema validates legacy emissions that predate the field.
  // Persisted to macro_prediction_observables.is_headline column
  // (migration 0012). Per Brief Revision §3.2 (new rule 17).
  is_headline:      z.boolean().optional(),
}).superRefine((obs, ctx) => {
  // 2026-05-26 — scale-range guard. INDICATOR_SCALE_RANGES table above
  // lists known macro indicators with bounded value ranges. Out-of-range
  // baseline_value rejected per [[feedback_no_fallbacks]] — drop the
  // observable rather than persist a wrong-scale number that corrupts
  // world-state projection (the TNX ×10 incident root cause).
  const bare = obs.indicator.replace(/^\$/, '').toUpperCase();
  const range = INDICATOR_SCALE_RANGES[bare];
  if (range && (obs.baseline_value < range[0] || obs.baseline_value > range[1])) {
    ctx.addIssue({
      code:    z.ZodIssueCode.custom,
      path:    ['baseline_value'],
      message: `baseline_value ${obs.baseline_value} out of expected range [${range[0]}, ${range[1]}] for indicator ${obs.indicator} — likely a scale error (e.g. yield emitted as basis-points ×10 instead of raw percent)`,
    });
  }
});

export type PredictedObservableEmit = z.infer<typeof PredictedObservableEmitSchema>;

// ── Macro-prediction emit shape ───────────────────────────────────────────

// Catalyst-mode emit (predicted_catalyst_date + CI window required).
const CatalystEmitFields = z.object({
  prediction_mode:            z.literal('catalyst'),
  thesis_narrative:           z.string().min(1),
  catalyst_description:       z.string().min(1),
  catalyst_source:            z.string().min(1),
  predicted_catalyst_date:    z.string(),
  catalyst_date_ci_low_days:  z.number().int(),
  catalyst_date_ci_high_days: z.number().int(),
  catalyst_probability:       z.number().min(0).max(1),
  post_catalyst_window_days:  z.number().int().nonnegative(),
  // trend-mode fields explicitly null in catalyst mode
  horizon_days:               z.null().optional(),
  horizon_ci_low_days:        z.null().optional(),
  horizon_ci_high_days:       z.null().optional(),
});

// Trend-mode emit (horizon_days required, catalyst fields null).
const TrendEmitFields = z.object({
  prediction_mode:            z.literal('trend'),
  thesis_narrative:           z.string().min(1),
  horizon_days:               z.number().int().positive(),
  horizon_ci_low_days:        z.number().int(),
  horizon_ci_high_days:       z.number().int(),
  // catalyst-mode fields explicitly null in trend mode
  catalyst_description:       z.null().optional(),
  catalyst_source:            z.null().optional(),
  predicted_catalyst_date:    z.null().optional(),
  catalyst_date_ci_low_days:  z.null().optional(),
  catalyst_date_ci_high_days: z.null().optional(),
  catalyst_probability:       z.null().optional(),
  post_catalyst_window_days:  z.null().optional(),
});

const CommonEmitFields = z.object({
  predicted_observables: z.array(PredictedObservableEmitSchema).min(1),  // multi-observable threshold checked in orchestrator
});

export const MacroPredictionEmitSchema = z.discriminatedUnion('prediction_mode', [
  CatalystEmitFields.merge(CommonEmitFields),
  TrendEmitFields.merge(CommonEmitFields),
]);

export type MacroPredictionEmit = z.infer<typeof MacroPredictionEmitSchema>;

// Convenience array schema for the top-level OutputSchema field.
//
// Design doc :427 specifies "2-5 enforced". Schema loosened to .min(1) per
// Session 5T (2026-05-28) D15 — first V3 fire (chain_run_id 019e6cfe) errored
// at m.contrarian.refine because the LLM emitted only 1 macro_prediction. The
// prompt still requests 2-5 normatively; Zod is permissive as a safety net
// (mirrors the 2026-05-25 m-synthesis loosening pattern at
// shared/src/translators/market-emit-to-d1.ts:106-114). Downstream runtime
// validators must NEVER tighten beyond this baseline (mirror-validate.ts
// already permits any length on the path-level macro_predictions array).
export const MacroPredictionEmitArraySchema = z.array(MacroPredictionEmitSchema).min(1).max(5);
